import React, {
	useEffect, useRef, useState, useCallback
} from 'react';
import {
	Box,
	Menu,
	MenuItem,
	Checkbox,
	ListItemText,
	Typography
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import { useMediaQuery } from '@mui/system';
import { useLocation } from 'react-router';
import LinkIcon from '@mui/icons-material/Link';
import PropTypes from 'prop-types';
import Tables from '../Common/tables.js';
import {
	downloadIndividualDashboardData,
	getApprovalRequestEsign,
	getAuditTrail,
	getDashboardData,
	getDocumentData,
	getDocumentRedirectURL,
	getIndividualDashboardCount
} from '../../Services/api-config.js';
import customToast from '../Common/custom-toast.js';
import { formatDate } from '../../utils/common-table-function.js';
import Loading from '../Common/LoaderComponent/loader.js';
import { downloadBase64File, formatDataGridColumns, getErrorMessage } from '../../utils/util.js';
import { formatAmountColumn, formatAvatarGroup, formatDateAndTime } from '../../utils/common-column-function.js';
import { ReactComponent as ViewIcon } from '../../Assets/Images/passwordEye.svg';
import { ReactComponent as AuditTrailIcon } from '../../Assets/Images/AuditTrail.svg';
import { ReactComponent as Imports } from '../../Assets/Images/import.svg';
import { downloadPDF } from '../../utils/dowload-pdf.js';
import PaymentDetailsModal from '../Common/Modal/payment-details-modal.js';
import TabHeader from '../Common/tab-header.js';
import PDFDetails from './Dashboard/OrganizationDashboard/pdf-details.js';
import DatePickerDialog from './Dashboard/OrganizationDashboard/date-picker-dialog.js';

const countMappingNames = {
	'Payment Pending': {
		name: 'paymentPending',
		color: '#EFAC00'
	},
	'Awaiting Signing': {
		name: 'awaitingSigning',
		color: '#DADDD3'
	},
	'Customer Rejected': {
		name: 'customerRejected',
		color: '#E96666'
	},
	Signed: {
		name: 'signed',
		color: '#B7D423'
	},
	'Signing Expired': {
		name: 'signingExpired',
		color: '#E96666'
	},
	'Verification Failed': {
		name: 'verificationFailed',
		color: '#E96666'
	},
	Cancelled: {
		name: 'cancelled',
		color: '#E96666'
	}
};

const TabContentRenderer = ({ tabs, type = '' }) => {
	const isTabChange = useRef(true);
	const location = useLocation();
	const [dropdowns, setDropDowns] = useState(null);
	const [selectedDropdownValue, setSelectedDropdownValue] = useState(90);
	const [initialLoading, setInitialLoading] = useState(true);
	const { showToast } = customToast();
	const [selectedTab, setSelectedTab] = useState(0);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [downloadAnchor, setDownloadAnchor] = useState(null);
	const currentDate = new Date();
	const threeMonthsAgo = new Date();
	threeMonthsAgo.setMonth(currentDate.getMonth() - 3);
	const isMobile = useMediaQuery('(max-width:600px)');

	const [selectedQuickDate, setSelectedQuickDate] = useState({
		quickDateFrom: formatDate(threeMonthsAgo),
		quickDateTo: formatDate(currentDate)
	});
	const [isPaymentPopUpOpen, setIsPaymentPopUpOpen] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState({});

	const [previousDropdownValue, setPreviousDropdownValue] = useState(null);
	const [openDatePicker, setOpenDatePicker] = useState(false);
	const [pageCount, setPageCount] = useState(1);
	const [openPDF, setOpenPDF] = useState({ openPDF: false, PDFDetails: [] });
	const [sortingType, setSortingType] = useState({
		sortingType: '',
		sortingName: ''
	});

	const [filterSearch, setFilterSearch] = useState({
		field: '',
		value: ''
	});
	const [kebabMenuItem, setKebabMenuItem] = useState([]);
	const allStatuses = [];
	const [statusFilter, setStatusFilter] = useState([...allStatuses]);
	const [isFilter, setIsFilter] = useState(false);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [anchorEl, setAnchorEl] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [documentCounts, setDocumentCounts] = useState({});
	const apiRef = useGridApiRef();
	const open = Boolean(downloadAnchor);

	const handleDownloadModal = (event) => {
		setDownloadAnchor(event.currentTarget);
	};

	const itemMenu = [
		{
			title: 'XLSX',
			icon: SaveIcon,
			onClick: () => handleExport('xlsx'),
			fontSize: 'small'
		},
		{
			title: 'CSV',
			icon: SaveIcon,
			onClick: () => handleExport('csv'),
			fontSize: 'small'
		}
	];

	const handleExport = async (type) => {
		setIsLoading(true);
		try {
			const documentState = tabs[selectedTab].endpoint.split('/').pop();

			const params = {
				quickDateFrom: selectedQuickDate.quickDateFrom || undefined,
				quickDateTo: selectedQuickDate.quickDateTo || undefined,
				filterColumnName: filterSearch?.field || undefined,
				filterValue: filterSearch?.value || undefined,
				sortingType: sortingType.sortingType || undefined,
				sortingColumnName: sortingType.sortingName || undefined
			};

			if (dropdowns?.[tabs[selectedTab]?.keyName]?.length) {
				params.statusFilterValue = JSON.stringify(statusFilter);
			}

			const response = await downloadIndividualDashboardData(
				documentState,
				type,
				params
			);
			if (response?.status === 200) {
				let Filename = `${documentState}.${type}`;
				if (!selectedQuickDate.quickDateFrom) {
					Filename = `${documentState}_${selectedQuickDate.quickDateFrom}_${selectedQuickDate.quickDateTo}.${type}`;
				}

				if (response?.data) {
					downloadBase64File(
						response.data,
						Filename,
						type
					);
					showToast('success', getErrorMessage(response));
				} else {
					showToast('error', getErrorMessage(response));
				}
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
			return error;
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setDownloadAnchor(null);
	};

	// Open filter menu
	const handleFilterClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	// Close filter menu
	const handleCloseFilterMenu = () => {
		setAnchorEl(null);
	};

	const handleView = async (params) => {
		try {
			setIsLoading(true);
			const response = await getDocumentData(
				params.documentId
			);
			if (response?.status === 200) {
				const allRecipients = response.data.flatMap((rec) => rec.recipientData);
				const payload = {
					openPDF: true,
					PDFDetails: {
						...response.data[0],
						recipientData: allRecipients,
						documentId: params.documentId,
						tabs: 'view',
						signed: params?.paymentStatus
					}
				};
				setOpenPDF(payload);
			} else if (response?.status === 404) {
				showToast('error', getErrorMessage(response));
			} else {
				showToast(
					'error',
					'Oops, something went wrong. Please try again later.'
				);
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleAuditTrail = async (selecteditem) => {
		try {
			setIsLoading(true);
			const response = await getAuditTrail(selecteditem.documentId);
			if (response.status === 200) {
				downloadPDF(
					'data:application/pdf;base64,' + response.data,
					selecteditem?.documentName
				);
				showToast('success', 'File Downloaded successfully');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleDownloadPDF = async (params) => {
		setIsLoading(true);
		try {
			const response = await getApprovalRequestEsign(params.documentId);
			if (response?.status === 200) {
				if (response.data[0]?.documentPdf) {
					downloadBase64File(
						response.data[0]?.documentPdf,
						response.data[0]?.documentName,
						'pdf',
						'application'
					);
					showToast('success', 'Downloaded successfully');
				} else {
					showToast('error', 'Document not found');
				}
			} else if (response?.status === 404) {
				showToast('error', getErrorMessage(response));
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleAction = async (documentId = '') => {
		setIsLoading(true);
		try {
			const response = await getDocumentRedirectURL(documentId);
			if (response.status === 200) {
				window.open(response.data.redirectUrl, '_blank');
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handlePaymentDetailsView = (paymentDetails) => {
		const {
			additionalAmountDescription,
			additionalamount,
			stampAmount,
			amountPayeeRecipientid,
			recipientData
		} = paymentDetails;

		const matchedRecipient = recipientData.find(
			(recipient) => recipient.recipientId === amountPayeeRecipientid

		);
		let recipientFirstName = '';
		let recipientLastName = '';
		let recipientMiddleName = '';
		if (matchedRecipient) {
			recipientFirstName = matchedRecipient.recipientFirstName;
			recipientMiddleName = matchedRecipient.recipientMiddleName;
			recipientLastName = matchedRecipient.recipientLastName;
		}

		const formattedStampAmount
			= amountPayeeRecipientid
 === null ? '' : stampAmount;

		setPaymentDetails({
			description: additionalAmountDescription,
			additionalamount,
			stampAmount: stampAmount === '-' ? '' : formattedStampAmount,
			recipientName: [
				recipientFirstName,
				recipientMiddleName,
				recipientLastName
			]
				.filter(Boolean)
				.join(' ')
		});

		setIsPaymentPopUpOpen(true);
	};

	// Fetch data based on selected tab
	const fetchData = async (emptyStatusFilter = false, filters = []) => {
		if (!initialLoading) {
			setIsLoading(true);
		}

		try {
			const selectedTabEndpoint = tabs[selectedTab].endpoint;

			const params = {
				page: pageCount,
				limit: pageSize,
				quickDateFrom: selectedQuickDate.quickDateFrom || undefined,
				quickDateTo: selectedQuickDate.quickDateTo || undefined,
				filterColumnName: filterSearch?.field || undefined,
				filterValue: filterSearch?.value || undefined,
				sortingType: sortingType.sortingType || undefined,
				sortingColumnName: sortingType.sortingName || undefined
			};

			if (dropdowns?.[tabs[selectedTab]?.keyName]?.length || filters.length > 0) {
				const tempStatusFilter = statusFilter.length > 0 || emptyStatusFilter ? statusFilter : filters;
				params.statusFilterValue = JSON.stringify(tempStatusFilter);
			}

			const response = await getDashboardData(selectedTabEndpoint, params);
			const { columns: apiColumns, result, totalCount: total } = response.data;
			const formattedColumns = formatDataGridColumns(apiColumns);
			const items = [];
			if (['signed', 'waititng-for-others', 'unsuccessful-workflows'].includes(type)) {
				items.push({
					title: 'View',
					icon: ViewIcon,
					onClick(row) {
						if (['Payment Pending', 'Payment Failed', 'Payment Expired'].includes(tabs[selectedTab]?.value)) {
							handlePaymentDetailsView(row);
						} else {
							handleView(row);
						}
					},
					fontSize: 'small'
				});
			}

			if (['action-required'].includes(type)) {
				items.push({
					title: 'Redirect',
					icon: LinkIcon,
					onClick(row) {
						handleAction(row?.documentId);
					},
					fontSize: 'small'
				});
			}

			if (['signed'].includes(type)) {
				items.push(
					{
						title: 'Audit trail',
						icon: AuditTrailIcon,
						onClick: (row) => handleAuditTrail(row),
						fontSize: 'small'
					},
					{
						title: 'Download PDF',
						icon: Imports,
						onClick: (row) => handleDownloadPDF(row),
						fontSize: 'small'
					}
				);
			}

			setKebabMenuItem(items);

			const formattedRows = result.map((row, index) => ({
				id: row.documentId || index,
				...row
			}));
			setColumns(formattedColumns);
			setRows(formattedRows);
			setTotalCount(total);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			isTabChange.current = false;
			setInitialLoading(false);
			setIsLoading(false);
		}
	};

	const handleTabChange = (_, newValue) => {
		isTabChange.current = true;
		setSelectedTab(newValue);

		// Reset all necessary states
		setInitialLoading(true);
		setPageCount(1);
		setPageSize(10);
		setSortingType({ sortingType: '', sortingName: '' });
		setFilterSearch({ field: '', value: '', operator: '' });
		setIsFilter(false);
		setColumns([]);
		setRows([]);
	};

	const handleApply = () => {
		if (startDate && endDate) {
			const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
			const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
			setPageCount(1);
			setPageSize(10);
			if (formattedStartDate > formattedEndDate) {
				showToast(
					'error',
					'Invalid date range. Please select valid start and end dates.'
				);
			} else {
				setStartDate(formattedStartDate);
				setEndDate(formattedEndDate);

				setSelectedQuickDate({
					quickDateFrom: formattedStartDate,
					quickDateTo: formattedEndDate
				});

				setOpenDatePicker(false);
			}
		} else {
			showToast(
				'error',
				'Invalid date range. Please select valid start and end dates.'
			);
		}
	};

	const handleDropdownChange = (event) => {
		const selectedValue = event.target.value;
		setPreviousDropdownValue(selectedDropdownValue);
		setSelectedDropdownValue(selectedValue);
		setPageCount(1);
		setPageSize(10);
		if (selectedValue !== 100) {
			setStartDate(null);
			setEndDate(null);
			const today = new Date();
			const previousDate = new Date(today);
			previousDate.setDate(today.getDate() - selectedValue);
			const quickDateFrom = formatDate(previousDate);
			const quickDateTo = formatDate(today);

			setSelectedQuickDate({ quickDateFrom, quickDateTo });
		} else if (selectedValue === 100) {
			setOpenDatePicker(true);
			setSelectedQuickDate({ quickDateFrom: null, quickDateTo: null });
		}
	};

	const onFilterChange = useCallback(
		(filterModel) => {
			const items = filterModel?.items?.filter(Boolean) || [];
			const firstItem = items[0];
			setPageCount(1);

			if (firstItem) {
				const { field = '', value = '', operator = '' } = firstItem;

				setIsFilter(true);
				setFilterSearch((prevFilter) => ({
					field,
					value: prevFilter.field && prevFilter.field !== field ? '' : value,
					operator
				}));
			} else if (isFilter) {
				setIsFilter(false);
				setFilterSearch({
					field: '',
					value: '',
					operator: ''
				});
			}
		},
		[isFilter]
	);

	const handleSortModelChange = (sortModel) => {
		const sortingType = sortModel?.length > 0 ? sortModel?.[0]?.sort : '';
		const sortingName = sortModel?.length > 0 ? sortModel?.[0]?.field : '';
		setSortingType({ sortingType, sortingName });
	};

	const handleStartDateChange = (date) => {
		setStartDate(date);
	};

	const handleEndDateChange = (date) => {
		setEndDate(date);
	};

	const updatePageCount = (count, size) => {
		setPageCount(count);
		setPageSize(size);
	};

	const fetchDocumentCount = async () => {
		try {
			const response = await getIndividualDashboardCount(type, {quickDateFrom: selectedQuickDate.quickDateFrom, quickDateTo: selectedQuickDate.quickDateTo});
			if (response.status === 200) {
				setDocumentCounts(response.result);
				const tempDropdown = response?.result?.statusDropdown;
				setDropDowns(tempDropdown);
				setStatusFilter(tempDropdown?.[tabs[selectedTab]?.keyName] || []);
				fetchData(false, tempDropdown?.[tabs[selectedTab]?.keyName]);
			} else {
				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		}
	};

	useEffect(() => {
		let getData;

		if (!isTabChange.current) {
			getData = setTimeout(() => {
				fetchData(true);
			}, 600);
		}

		return () => {
			if (getData) {
				clearTimeout(getData);
			}
		};
	}, [filterSearch.value, JSON.stringify(statusFilter)]);

	useEffect(() => {
		if (!isTabChange.current) {
			fetchData();
		}
	}, [JSON.stringify(sortingType)]);

	useEffect(() => {
		// Trigger your reset logic or fetchData here
		// Optional: You can reset tab or filters if needed
		isTabChange.current = true;
		setSelectedTab(0);
		setStatusFilter(tabs[0]?.statusFilterOptions || []);
		setInitialLoading(true);
		setSelectedDropdownValue(90);
		setStartDate(null);
		setEndDate(null);
		setSelectedQuickDate({
			quickDateFrom: formatDate(threeMonthsAgo),
			quickDateTo: formatDate(currentDate)
		});
		setPreviousDropdownValue(null);
		setDocumentCounts({});
		setPageCount(1);
		setPageSize(10);
		setSortingType({ sortingType: '', sortingName: '' });
		setFilterSearch({ field: '', value: '', operator: '' });
		setIsFilter(false);
		setColumns([]);
		setRows([]);
	}, [location.pathname]);

	useEffect(() => {
		fetchDocumentCount();
	}, [
		selectedTab,
		selectedQuickDate.quickDateFrom,
		selectedQuickDate.quickDateTo,
		pageCount,
		pageSize,
		location.pathname
	]);

	const formatDashboardStatusColumn = (params) => {
		if (params.row.status) {
			let color;
			let displayVal;
			let additionalContent;

			switch (params.row.status) {
			case 'Pending for checker approval':
			case 'Pending for protean approval':
			case 'Pending for fin-ops approval':
			case 'Pending for vendor procurement':
			case 'Pending for vendor re-upload':
			case 'Order confirmed by protean':
			case 'Drafts':
			case 'Awaiting signing':
			case 'Pending for protean confirmation': {
				color = '#EFAC00';
				break;
			}

			case 'Procurement completed':
			case 'Procured':
			case 'Signed': {
				color = '#B7D423';
				break;
			}

			case 'Bulk e-Sign': {
				color = '#0a0a0a';
				break;
			}

			case 'Rejected by protean':
			case 'Rejected by fin-ops':
			case 'Rejected by checker':
			case 'Order cancel by protean':
			case 'Customer Rejected': {
				color = '#E96666';
				break;
			}

			default: {
				color = '';
			}
			}

			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'center',
						alignItems: 'center'
					}}
				>
					<Box sx={{ color }}>{displayVal || params?.row?.status}</Box>
					{additionalContent}
				</Box>
			);
		}

		return null;
	};

	const tempColumns = columns.map((column) => ({
		...column,
		renderCell(params) {
			const amountFields = [
				'stampAmount',
				'stampDuty',
				'totalStampValue',
				'orderValue',
				'value'
			];
			const dateFields = [
				'updatedAt',
				'approvedAt',
				'recipientSentAt',
				'approvalSentAt',
				'orderedOn',
				'response',
				'rejectedOn',
				'receivedDate',
				'proteanReceivedAt',
				'proteanUpdatedAt',
				'forwardedOn',
				'deliveredOn',
				'createdAt',
				'vendorReceivedAt'
			];
			let content = (
				<div className='dataGrid-value-resp'>{params.row[column.field]}</div>
			);

			if (amountFields.includes(column.field)) {
				content = formatAmountColumn(params?.row[column.field]);
			} else if (column.field === 'recipientData') {
				content = formatAvatarGroup(params.row.recipientData);
			} else if (dateFields.includes(column.field)) {
				content = formatDateAndTime(params.row[column.field]);
			} else if (column.field === 'denominationDetails') {
				content = (
					<Box
						display={'flex'}
						flexDirection={'column'}
						height={'100%'}
						justifyContent={'center'}
					>
						{params?.row?.denominationDetails?.map((val) => (
							<Box key={val.denomination}>{val.denomination}</Box>
						))}
					</Box>
				);
			} else if (column.field === 'status') {
				content = formatDashboardStatusColumn(params);
			} else if (
				column.field === 'quantity'
				&& params?.row?.denominationDetails
			) {
				content = (
					<Box
						display={'flex'}
						flexDirection={'column'}
						height={'100%'}
						justifyContent={'center'}
					>
						{params?.row?.denominationDetails?.map((val, i) => (
							<Box key={val.denomination + i}>{val.quantity}</Box>
						))}
					</Box>
				);
			} else if (column.field === 'stampTotalValue') {
				content = (
					<Box
						display={'flex'}
						flexDirection={'column'}
						height={'100%'}
						justifyContent={'center'}
					>
						{params?.row?.denominationDetails?.map((val, i) => (
							<Box key={val.denomination + i}>{val.stampTotalValue}</Box>
						))}
					</Box>
				);
			}

			return content;
		}
	}));

	return (
		<Box sx={{ width: '100%', mb: 2 }}>
			<Loading open={isLoading} />
			<PDFDetails
				open={openPDF.openPDF}
				onClose={() => setOpenPDF({ openPDF: false })}
				file={openPDF.PDFDetails}
				view={true}
				hideAction={true}
				updateLoading={() => {}}
				showSign={true}
				showInfo={type === 'waititng-for-others'}
				rejectedTab={false}
			/>
			<PaymentDetailsModal
				open={isPaymentPopUpOpen}
				onClose={() => {
					setIsPaymentPopUpOpen(false);
					setPaymentDetails({});
				}}
				paymentDetails={paymentDetails}
				isMobile={isMobile}
			/>

			<TabHeader
				isMobile={isMobile}
				tabs={tabs}
				selectedTab={selectedTab}
				handleTabChange={handleTabChange}
				documentCounts={documentCounts}
				countMappingNames={countMappingNames}
				selectedDropdownValue={selectedDropdownValue}
				handleDropdownChange={handleDropdownChange}
				startDate={startDate}
				endDate={endDate}
				statusFilter={statusFilter}
				handleFilterClick={handleFilterClick}
				handleDownloadModal={handleDownloadModal}
				downloadAnchor={downloadAnchor}
				open={open}
				handleClose={handleClose}
				itemMenu={itemMenu}
				dropdowns={dropdowns}
			/>

			{/* Date Picker */}
			<DatePickerDialog
				open={openDatePicker}
				onClose={() => {
					setOpenDatePicker(false);
					setEndDate(null);
					setStartDate(null);
					setSelectedDropdownValue(previousDropdownValue);
				}}
				startDate={startDate ? dayjs(startDate) : null}
				endDate={endDate ? dayjs(endDate) : null}
				handleStartDateChange={handleStartDateChange}
				handleEndDateChange={handleEndDateChange}
				handleApply={handleApply}
			/>

			{dropdowns?.[tabs[selectedTab]?.keyName]?.length && (() => {
				const statusOptions = dropdowns?.[tabs[selectedTab]?.keyName] || [];
				const disableSelectAll = statusFilter.length === statusOptions.length;
				const disableClearAll = statusFilter.length === 0;

				return (
					<Menu
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleCloseFilterMenu}
						PaperProps={{ style: { padding: 8, minWidth: 220 } }}
					>
						{/* Select All / Clear All Actions */}
						<Box display='flex' justifyContent='space-between' px={1.5} py={0.5}>
							<Typography
								variant='body2'
								color={disableSelectAll ? 'text.disabled' : 'primary'}
								sx={{
									cursor: disableSelectAll ? 'default' : 'pointer',
									fontWeight: 500,
									pointerEvents: disableSelectAll ? 'none' : 'auto',
									opacity: disableSelectAll ? 0.5 : 1
								}}
								onClick={() => {
									if (!disableSelectAll) {
										setStatusFilter(statusOptions);
									}
								}}
							>
          Select All
							</Typography>

							<Typography
								variant='body2'
								color={disableClearAll ? 'text.disabled' : 'error'}
								sx={{
									cursor: disableClearAll ? 'default' : 'pointer',
									fontWeight: 500,
									pointerEvents: disableClearAll ? 'none' : 'auto',
									opacity: disableClearAll ? 0.5 : 1
								}}
								onClick={() => {
									if (!disableClearAll) {
										setStatusFilter([]);
									}
								}}
							>
          Clear All
							</Typography>
						</Box>

						{/* Filter options */}
						{statusOptions.map((status) => {
							const isSelected = statusFilter.includes(status);
							return (
								<MenuItem
									key={status}
									onClick={() => {
										const newStatusFilter = isSelected
											? statusFilter.filter((item) => item !== status)
											: [...statusFilter, status];
										setStatusFilter(newStatusFilter);
									}}
								>
									<Checkbox checked={isSelected} />
									<ListItemText primary={status} />
								</MenuItem>
							);
						})}
					</Menu>
				);
			})()}

			{initialLoading && <Loading open={initialLoading} />}
			{!initialLoading && (
				<Box width='100%' sx={{ paddingX: '20px' }} paddingTop='20px' mb='20px'>
					<Tables
						apiRef={apiRef}
						updatePageCount={updatePageCount}
						pageCount={pageCount}
						pageSize={pageSize}
						rowsPerPageRender={true}
						totalRowCount={totalCount}
						columns={tempColumns}
						rows={rows}
						showDelete={true}
						showEdit={true}
						showInfo
						kebabMenuItem={kebabMenuItem}
						onFilterChange={onFilterChange}
						marginX='0px'
						showCustomCol={true}
						tableStatus={true}
						handleSortModelChange={handleSortModelChange}
						filterSearch={filterSearch}
						isDashboard
					/>
				</Box>
			)}
		</Box>
	);
};

export default TabContentRenderer;

TabContentRenderer.propTypes = {
	type: PropTypes.string.isRequired,
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			value: PropTypes.string.isRequired,
			endpoint: PropTypes.string.isRequired
		})
	).isRequired
};
